import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useTranslation } from 'react-i18next';

interface AITaskConfirmationModalProps {
    isOpen: boolean;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    onConfirm: () => void;
    onCancel: () => void;
}

export const AITaskConfirmationModal: React.FC<AITaskConfirmationModalProps> = ({
    isOpen,
    title,
    message,
    confirmText,
    cancelText,
    onConfirm,
    onCancel,
}) => {
    const { t } = useTranslation("common");
    const titleRef = React.useRef(null);

    const finalConfirmText = confirmText || t("actions.delete");
    const finalCancelText = cancelText || t("actions.cancel");

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-[100] dark" onClose={onCancel} initialFocus={titleRef}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="relative bg-neutral-900/95 rounded-2xl shadow-[0_25px_60px_-15px_rgba(0,0,0,0.6)] max-w-sm w-full p-8 transform transition-all border border-white/10">
                                {/* Icon */}
                                <div className={`flex items-center justify-center w-16 h-16 mx-auto mb-6 bg-rose-500/10 rounded-full transition-colors duration-300`}>
                                    <span className="w-8 h-8 text-rose-500" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_63308e57.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_63308e57.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                </div>

                                {/* Content */}
                                <div className="text-center mb-8">
                                    <Dialog.Title
                                        as="h3"
                                        className="text-xl font-bold text-white mb-2 focus:outline-none"
                                        ref={titleRef}
                                        tabIndex={-1}
                                    >
                                        {title}
                                    </Dialog.Title>
                                    <p className="text-sm text-white/50 leading-relaxed">{message}</p>
                                </div>

                                {/*Buttons */}
                                <div className="flex gap-3">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onCancel();
                                        }}
                                        className="flex-1 px-4 py-3 text-sm font-medium text-white/50 hover:text-white bg-white/5 hover:bg-white/10 rounded-xl focus:outline-none transition-all"
                                    >
                                        {finalCancelText}
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onConfirm();
                                        }}
                                        className="flex-1 px-4 py-3 text-sm font-medium text-white bg-rose-500 hover:bg-rose-600 rounded-xl focus:outline-none transition-all shadow-lg"
                                    >
                                        {finalConfirmText}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
